// =========================================================================
// HelpyDoc Metabolic · Skeleton — Placeholders animados de carga
// Uso: <SkeletonDashboard /> en TodayPage, <SkeletonLogros /> en LogrosPage
// =========================================================================
import type { CSSProperties } from 'react';

// ── Bloque base ───────────────────────────────────────────────────────────

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  style?: CSSProperties;
  className?: string;
}

export function Skeleton({ width = '100%', height = 16, style, className = '' }: SkeletonProps) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{ width, height, ...style }}
      aria-hidden="true"
    />
  );
}

// ── Dashboard (TodayPage) ─────────────────────────────────────────────────

export function SkeletonDashboard() {
  return (
    <div style={{ padding: '6px 16px 16px' }} aria-label="Cargando…" aria-busy="true">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingTop: 6 }}>
        <div>
          <Skeleton width={150} height={18} style={{ marginBottom: 8 }} />
          <Skeleton width={110} height={12} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Skeleton width={38} height={38} style={{ borderRadius: 12 }} />
          <Skeleton width={38} height={38} style={{ borderRadius: 12 }} />
        </div>
      </div>

      {/* Quick actions 2×2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11, marginBottom: 20 }}>
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} height={72} style={{ borderRadius: 20 }} />
        ))}
      </div>

      {/* Section title */}
      <Skeleton width={130} height={15} style={{ marginBottom: 14 }} />

      {/* Tab bar */}
      <Skeleton height={44} style={{ borderRadius: 14, marginBottom: 12 }} />

      {/* Content card */}
      <Skeleton height={200} style={{ borderRadius: 16 }} />
    </div>
  );
}

// ── Logros (LogrosPage) ───────────────────────────────────────────────────

export function SkeletonLogros() {
  return (
    <div style={{ padding: '4px 0' }} aria-label="Cargando logros…" aria-busy="true">
      <Skeleton height={80} style={{ borderRadius: 16, marginBottom: 16 }} />
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
          <Skeleton width={52} height={52} style={{ borderRadius: 26, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Skeleton width="55%" height={14} style={{ marginBottom: 7 }} />
            <Skeleton width="75%" height={11} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Actividad (ActividadFisicaPage) ─────────────────────────────────────────

export function SkeletonActividad() {
  return (
    <div className="actividad-area" aria-label="Cargando actividad…" aria-busy="true">
      <Skeleton height={140} style={{ borderRadius: 20, marginBottom: 12 }} />
      <Skeleton height={180} style={{ borderRadius: 16, marginBottom: 12 }} />
      <Skeleton height={120} style={{ borderRadius: 16 }} />
    </div>
  );
}

// ── Perfil (PerfilPage) ─────────────────────────────────────────────────────

export function SkeletonPerfil() {
  return (
    <div className="perfil-area" aria-label="Cargando perfil…" aria-busy="true">
      <Skeleton height={72} style={{ borderRadius: 16, marginBottom: 12 }} />
      <Skeleton height={220} style={{ borderRadius: 16, marginBottom: 12 }} />
      <Skeleton height={88} style={{ borderRadius: 16, marginBottom: 12 }} />
    </div>
  );
}
